/**
 * @fileoverview Checkbox - Form checkbox component
 *
 * Purpose: Accessible checkbox for form selections and multi-select
 * Dependencies: Radix UI Checkbox primitives, lucide-react
 * Used by: Forms, settings, multi-select lists, filters
 *
 * Features:
 * - Checked, unchecked, and indeterminate states
 * - Keyboard accessible
 * - Focus ring indicators
 * - Disabled state support
 * - Animated check icon
 *
 * @example
 * <Checkbox checked={isChecked} onCheckedChange={setIsChecked} />
 *
 * @example
 * // With label
 * <div className="flex items-center space-x-2">
 *   <Checkbox id="terms" />
 *   <label htmlFor="terms">Accept terms and conditions</label>
 * </div>
 */

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";

import { cn } from "@/utils/misc";

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer grid h-4 w-4 shrink-0 place-content-center rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=indeterminate]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:text-primary-foreground",
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn("grid place-content-center text-current")}
    >
      {props.checked === "indeterminate" ? (
        <Minus strokeWidth={3} className="h-3 w-3" />
      ) : (
        <Check strokeWidth={3} className="h-3 w-3" />
      )}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
